// frontend/src/components/Mobile/FastReact/MobileRestock.jsx
//
// Mobile version of Reabastecer.
//   1. scan (or type) the order QR  →  "TC22600492|10400|100|S123"
//        field 1 = po_code, field 2 = po_code_line  →  po_code_line_full = "TC22600492-10400"
//   2. the order's fr_setup_fios_preparation lines are listed
//   3. "Reabastecer" on a line opens a pop-up for num_restock_cones and marks
//      the line RESTOCK (same endpoint / rules as the desktop Reabastecer:
//      only finished — COMPLETED — lines can be restocked).

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { fastReactAPI } from '../../../api/client';
import { FiCamera, FiImage, FiRepeat, FiEdit2, FiRotateCcw, FiX, FiAlertCircle, FiPackage, FiSearch } from 'react-icons/fi';
import MobileLayout from '../MobileLayout';
import useQrScanner from '../useQrScanner';
import Toast from '../../Common/Toast';
import { useToast } from '../../../hooks/useToast';
import '../Mobile.css';

// "TC22600492|10400|100|S123"  → "TC22600492-10400"
// "TC22600492-10400" (typed)    → "TC22600492-10400"
// Returns null when it can't be read as an order.
export const parseOrderCode = (raw) => {
  const text = String(raw || '').trim().toUpperCase();
  if (!text) return null;

  let po = null;
  let line = null;
  if (text.includes('|')) {
    const f = text.split('|').map((s) => s.trim());
    po = f[0];
    line = f[1];
  } else {
    const m = text.match(/^(.+)-(\d+)$/);
    if (m) { po = m[1].trim(); line = m[2]; }
  }
  if (!po || !line || !/^\d+$/.test(line)) return null;
  // po_code_line_full uses a 5-digit line (e.g. TC22600492-10400)
  return `${po}-${line.padStart(5, '0')}`;
};

const fmtQty = (v) =>
  v != null
    ? parseFloat(v).toLocaleString('pt-PT', { minimumFractionDigits: 1, maximumFractionDigits: 1 })
    : '—';

const itemBadge = (s) =>
  s === 'SETUP'
    ? <span className="mb-badge mb-badge--setup">SETUP</span>
    : s === 'IN_PROGRESS'
      ? <span className="mb-badge mb-badge--inprog">Em Prep.</span>
      : s === 'READY'
        ? <span className="mb-badge mb-badge--completed">Pronto p/ Recolha</span>
        : s === 'COMPLETED'
          ? <span className="mb-badge mb-badge--completed">Recolhido</span>
          : s === 'RESTOCK'
            ? <span className="mb-badge mb-badge--restock">Reabastecer</span>
            : null;

const MobileRestock = () => {
  const { toasts, removeToast, toast_success, toast_error } = useToast();

  const [input, setInput] = useState('');
  const [looking, setLooking] = useState(false);
  const [lookupError, setLookupError] = useState('');

  const [setup, setSetup] = useState(null);   // row from plano-teares
  const [items, setItems] = useState([]);

  // pop-up
  const [pendingItem, setPendingItem] = useState(null);
  const [conesInput, setConesInput] = useState('');
  const [popupMode, setPopupMode] = useState('create'); // 'create' | 'edit'
  const [clearItem, setClearItem] = useState(null);     // line waiting for "anular reabastecimento" confirmation
  const [saving, setSaving] = useState(false);
  const conesRef = useRef(null);

  // Height of the area NOT covered by the on-screen keyboard, so the pop-up is
  // centred in what the user can actually see (window.visualViewport shrinks
  // when the keyboard opens; fixed elements otherwise stay behind it).
  const [visibleHeight, setVisibleHeight] = useState(null);
  useEffect(() => {
    if (!pendingItem) return undefined;
    const vv = window.visualViewport;
    if (!vv) return undefined;
    const update = () => setVisibleHeight(vv.height);
    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
      setVisibleHeight(null);
    };
  }, [pendingItem]);

  // ── Lookup ────────────────────────────────────────────────────────────────
  const lookup = useCallback(async (raw) => {
    setLookupError('');
    const full = parseOrderCode(raw);
    if (!full) {
      setLookupError('Código inválido. Esperado: ORDEM|LINHA|… ou ORDEM-LINHA.');
      return;
    }

    setLooking(true);
    setSetup(null);
    setItems([]);
    try {
      const r = await fastReactAPI.getPlanoTeares({
        po_code: full, print_status: 'false', page: 1, page_size: 10,
      });
      const row = (r.data.data || []).find(
        (x) => String(x.po_code_line_full || '').trim().toUpperCase() === full
      );
      if (!row) {
        setLookupError(`Ordem "${full}" não encontrada.`);
        return;
      }

      const ir = await fastReactAPI.getPreparationItems(row.po_setup_id);
      setSetup(row);
      setItems(ir.data.items || []);
      setInput(full);
    } catch (e) {
      setLookupError(e.response?.data?.error || 'Erro ao procurar a ordem.');
    } finally {
      setLooking(false);
    }
  }, []);

  // ── QR camera ─────────────────────────────────────────────────────────────
  const scanner = useQrScanner((text) => {
    setInput(text.toUpperCase());
    lookup(text);
  });

  const [showCamList, setShowCamList] = useState(false);
  const toggleCamList = async () => {
    if (showCamList) { setShowCamList(false); return; }
    setShowCamList(true);
    await scanner.loadCameras();
  };
  const pickCamera = (deviceId) => {
    setShowCamList(false);
    scanner.startWith(deviceId);
  };

  const photoRef = useRef(null);
  const handlePhoto = (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow taking the same picture again
    if (file) scanner.decodeFile(file);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    lookup(input);
  };

  const reset = () => {
    scanner.stop();
    setInput('');
    setSetup(null);
    setItems([]);
    setLookupError('');
  };

  // ── Restock pop-up ────────────────────────────────────────────────────────
  const openRestock = (item, mode = 'create') => {
    setPopupMode(mode);
    setConesInput(item.num_restock_cones != null ? String(item.num_restock_cones) : '');
    setPendingItem(item);
    setTimeout(() => conesRef.current?.focus(), 80);
  };

  const closeRestock = () => {
    if (saving) return;
    setPendingItem(null);
  };

  const confirmRestock = async () => {
    if (!pendingItem || saving) return;
    const cones = parseInt(conesInput, 10);
    if (!Number.isFinite(cones) || cones < 0) {
      toast_error('Indique um número de cones válido.');
      return;
    }

    setSaving(true);
    try {
      // edit = only correct the cone count (does not restart the restock request)
      if (popupMode === 'edit') {
        const er = await fastReactAPI.updateRestockCones(pendingItem.preparation_id, cones);
        if (er.data.success) {
          setItems((prev) => prev.map((it) =>
            it.preparation_id === pendingItem.preparation_id ? { ...it, num_restock_cones: er.data.num_restock_cones } : it
          ));
          toast_success('Número de cones atualizado');
          setPendingItem(null);
        }
        return;
      }

      const r = await fastReactAPI.markRestock(pendingItem.preparation_id, cones);
      if (r.data.success) {
        const { status_line, num_restock_cones, po_setup_status } = r.data;
        // container_material is cleared for the whole order on the backend
        setItems((prev) =>
          prev.map((it) =>
            it.preparation_id === pendingItem.preparation_id
              ? { ...it, status_line, num_restock_cones, container_material: '' }
              : { ...it, container_material: '' }
          )
        );
        setSetup((prev) => (prev ? { ...prev, status: po_setup_status ?? prev.status, container_material: null } : prev));
        toast_success('Linha marcada para reabastecimento');
        setPendingItem(null);
      }
    } catch (e) {
      toast_error(e.response?.data?.error || 'Erro ao marcar reabastecimento');
    } finally {
      setSaving(false);
    }
  };

  // Undo a restock marked by mistake → line back to COMPLETED, cones removed
  const confirmClear = async () => {
    if (!clearItem || saving) return;
    setSaving(true);
    try {
      const r = await fastReactAPI.clearRestock(clearItem.preparation_id);
      if (r.data.success) {
        setItems((prev) => prev.map((it) =>
          it.preparation_id === clearItem.preparation_id
            ? { ...it, status_line: 'COMPLETED', num_restock_cones: null }
            : it
        ));
        setSetup((prev) => (prev ? { ...prev, status: r.data.po_setup_status ?? prev.status } : prev));
        toast_success('Reabastecimento anulado');
        setClearItem(null);
      }
    } catch (e) {
      toast_error(e.response?.data?.error || 'Erro ao anular reabastecimento');
    } finally {
      setSaving(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  const sortedItems = [
    ...items.filter((i) => i.status_line !== 'READY' && i.status_line !== 'COMPLETED'),
    ...items.filter((i) => i.status_line === 'READY'),
    ...items.filter((i) => i.status_line === 'COMPLETED'),
  ];

  return (
    <MobileLayout title="Reabastecer">
      <canvas ref={scanner.canvasRef} style={{ display: 'none' }} />

      {/* Camera viewfinder — always in the DOM so the ref stays stable */}
      <div className={scanner.scanning ? '' : 'mb-cam-hidden'} style={scanner.scanning ? { marginBottom: 12 } : undefined}>
        <div style={{ position: 'relative', background: '#000', width: '100%', paddingTop: '100%' }}>
          <video
            ref={scanner.videoRef}
            autoPlay
            playsInline
            muted
            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'fill', display: 'block' }}
          />
          {scanner.scanning && !scanner.cameraReady && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.6)' }}>
              <span style={{ color: 'white', fontSize: 13 }}>A iniciar câmara…</span>
            </div>
          )}
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
            <div style={{ width: '58%', aspectRatio: '1', position: 'relative' }}>
              {[['top', 'left'], ['top', 'right'], ['bottom', 'left'], ['bottom', 'right']].map(([v, h]) => (
                <div key={v + h} style={{
                  position: 'absolute', [v]: 0, [h]: 0, width: 20, height: 20,
                  borderTop: v === 'top' ? '3px solid var(--primary)' : 'none',
                  borderBottom: v === 'bottom' ? '3px solid var(--primary)' : 'none',
                  borderLeft: h === 'left' ? '3px solid var(--primary)' : 'none',
                  borderRight: h === 'right' ? '3px solid var(--primary)' : 'none',
                }} />
              ))}
            </div>
          </div>
          <button type="button" onClick={scanner.stop}
            style={{ position: 'absolute', top: 10, right: 10, display: 'flex', alignItems: 'center', gap: 5, background: 'rgba(0,0,0,0.65)', border: 'none', color: 'white', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', fontSize: 13 }}>
            <FiX size={14} /> Cancelar
          </button>
          {scanner.cameraCount > 1 && (
            <button type="button" onClick={scanner.switchCamera}
              style={{ position: 'absolute', top: 10, left: 10, display: 'flex', alignItems: 'center', gap: 5, background: 'rgba(0,0,0,0.65)', border: 'none', color: 'white', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', fontSize: 13 }}>
              <FiRepeat size={14} /> Trocar câmara
            </button>
          )}
          <div style={{ position: 'absolute', bottom: 14, left: 0, right: 0, textAlign: 'center', color: 'white', fontSize: 12, textShadow: '0 1px 4px rgba(0,0,0,0.8)' }}>
            Aponte para o QR Code da ordem
          </div>
        </div>
      </div>

      {/* Scan / manual entry */}
      {!setup && !looking && (
        <div className="mb-scan-box">
          <div className="mb-scan-title">Leitura do QR Code</div>
          <div className="mb-scan-sub">
            Leia o QR code da ordem ou introduza o código (ex.: TC22600492-10400).
          </div>
          <form onSubmit={handleSubmit}>
            <div className="mb-scan-row">
              <input
                type="text"
                className="mb-scan-input"
                autoComplete="off"
                autoCapitalize="characters"
                value={input}
                onChange={(e) => setInput(e.target.value.toUpperCase())}
                disabled={scanner.scanning}
              />
              <button type="button"
                className={`mb-scan-cam-btn ${scanner.scanning ? 'mb-scan-cam-btn--on' : ''}`}
                onClick={scanner.scanning ? scanner.stop : scanner.start}
                title="Ler com câmara">
                {scanner.scanning ? <FiX size={20} /> : <FiCamera size={20} />}
              </button>
              {/* Fallback for devices whose live camera stays black: native camera app → decode photo */}
              <button type="button"
                className="mb-scan-cam-btn"
                onClick={() => photoRef.current?.click()}
                disabled={scanner.scanning}
                title="Tirar foto ao QR">
                <FiImage size={20} />
              </button>
              <input ref={photoRef} type="file" accept="image/*" capture="environment"
                style={{ display: 'none' }} onChange={handlePhoto} />
            </div>
            <button type="submit" className="mb-btn-primary" disabled={!input.trim() || scanner.scanning}>
              <FiSearch size={15} style={{ verticalAlign: 'middle', marginRight: 6 }} />
              Procurar
            </button>
          </form>

          {/* Manual camera choice — for phones with several rear cameras where the
              default one shows black (like the picker other browsers offer) */}
          <button type="button" onClick={toggleCamList}
            style={{ marginTop: 10, background: 'none', border: 'none', color: 'var(--primary)', fontSize: 12, fontWeight: 600, padding: 0, cursor: 'pointer' }}>
            {showCamList ? 'Fechar lista de câmaras' : 'Escolher câmara…'}
          </button>
          {showCamList && (
            <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
              {scanner.cameras.length === 0 && (
                <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>A procurar câmaras…</div>
              )}
              {scanner.cameras.map((c, i) => (
                <button key={c.deviceId} type="button" onClick={() => pickCamera(c.deviceId)}
                  style={{ textAlign: 'left', height: 42, padding: '0 12px', border: '1px solid var(--gray-300)', borderRadius: 10, background: 'white', fontSize: 13, cursor: 'pointer' }}>
                  📷 {c.label || `Câmara ${i + 1}`}
                </button>
              ))}
            </div>
          )}

          {scanner.debug.length > 0 && (
            <details style={{ marginTop: 10 }}>
              <summary style={{ fontSize: 11, color: 'var(--gray-500)', cursor: 'pointer' }}>Diagnóstico da câmara</summary>
              <pre style={{ fontSize: 10, whiteSpace: 'pre-wrap', color: 'var(--gray-600)', margin: '6px 0 0' }}>{scanner.debug.join('\n')}</pre>
            </details>
          )}
        </div>
      )}

      {(lookupError || scanner.error) && (
        <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 12, padding: 14, display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 12 }}>
          <FiAlertCircle size={18} style={{ color: 'var(--error)', flexShrink: 0, marginTop: 2 }} />
          <div style={{ fontWeight: 600, color: 'var(--error)', fontSize: 13 }}>
            {lookupError || scanner.error}
          </div>
        </div>
      )}

      {looking && (
        <div className="mb-state"><div className="mb-spinner" /><span>A procurar ordem…</span></div>
      )}

      {/* Order + lines */}
      {setup && (
        <>
          <div className="mb-detail-header">
            <div className="mb-detail-po">{setup.po_code_line_full}</div>
            {setup.product_name && (
              <div className="mb-detail-product">
                {setup.product_name}{setup.customer_name ? ` · ${setup.customer_name}` : ''}
              </div>
            )}
            <div className="mb-detail-meta">
              {setup.machine && <span>Máquina {setup.machine}</span>}
              {setup.status === 'RESTOCK' && <span className="mb-badge mb-badge--restock">REABASTECER</span>}
              {setup.status === 'COMPLETED' && <span className="mb-badge mb-badge--completed">CONCLUIDO</span>}
            </div>
            <button type="button" className="mb-btn-primary" style={{ marginTop: 12, height: 42 }} onClick={reset}>
              Nova leitura
            </button>
          </div>

          {sortedItems.length === 0 ? (
            <div className="mb-state"><span>Sem itens de preparação</span></div>
          ) : (
            <div className="mb-prep-list">
              {sortedItems.map((item) => {
                const canRestock = item.status_line === 'COMPLETED';
                return (
                  <div key={item.preparation_id} className="mb-prep-item">
                    <div className="mb-prep-info">
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                        <span className="mb-prep-code">{item.product_code}</span>
                        {item.variant_code && <span className="mb-prep-code"> - {item.variant_code}</span>}
                      </div>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', marginTop: 1 }}>
                        {item.lot_no && <span className="mb-prep-code">Lote: {item.lot_no}</span>}
                        {item.location_position && (
                          <span style={{ fontSize: 11, color: 'var(--gray-800)', background: 'var(--gray-100)', borderRadius: 4, padding: '1px 5px', fontFamily: 'monospace' }}>
                            📍 {item.location_position}
                          </span>
                        )}
                      </div>
                      {item.product_description && (
                        <div style={{ fontSize: 11, color: 'var(--gray-800)', marginTop: 1 }}>{item.product_description}</div>
                      )}
                      <div className="mb-prep-status-row" style={{ marginTop: 5 }}>
                        {itemBadge(item.status_line)}
                        {item.num_restock_cones != null && (
                          <span style={{ fontSize: 11, color: 'var(--gray-500)' }}>Cones {item.num_restock_cones}</span>
                        )}
                      </div>
                    </div>
                    <div className="mb-prep-right">
                      <span className="mb-prep-qty">{fmtQty(item.quantity)}</span>
                      {item.status_line === 'RESTOCK' ? (
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button type="button" className="mb-restock-btn" disabled={saving}
                            onClick={() => openRestock(item, 'edit')}
                            title="Corrigir número de cones">
                            <FiEdit2 size={14} /> Cones
                          </button>
                          <button type="button" className="mb-restock-btn mb-restock-btn--danger" disabled={saving}
                            onClick={() => setClearItem(item)}
                            title="Anular reabastecimento">
                            <FiRotateCcw size={14} /> Anular
                          </button>
                        </div>
                      ) : (
                        <button type="button" className="mb-restock-btn"
                          disabled={!canRestock || saving}
                          onClick={() => openRestock(item, 'create')}
                          title={canRestock ? 'Marcar para reabastecimento' : 'Só linhas recolhidas podem ser reabastecidas'}>
                          <FiPackage size={14} /> Reabastecer
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Pop-up: num_restock_cones */}
      {pendingItem && (
        <div
          style={{ position: 'fixed', top: 0, left: 0, right: 0, height: visibleHeight ? `${visibleHeight}px` : '100dvh', background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 16px', boxSizing: 'border-box', zIndex: 200 }}
          onClick={closeRestock}
        >
          <div
            style={{ background: 'white', width: '100%', maxWidth: 420, borderRadius: 16, padding: '20px', boxShadow: '0 4px 24px rgba(0,0,0,0.25)', maxHeight: '100%', overflowY: 'auto' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <div style={{ fontSize: 16, fontWeight: 700 }}>{popupMode === 'edit' ? '✏️ Corrigir cones' : '📦 Reabastecer'}</div>
              <button type="button" onClick={closeRestock}
                style={{ background: 'none', border: 'none', fontSize: 20, color: 'var(--gray-400)', cursor: 'pointer', padding: '0 4px', lineHeight: 1 }}>
                ×
              </button>
            </div>

            <div style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 13, marginBottom: 2 }}>
              {pendingItem.product_code}{pendingItem.variant_code ? ` - ${pendingItem.variant_code}` : ''}
              {pendingItem.lot_no ? ` · ${pendingItem.lot_no}` : ''}
            </div>
            <div style={{ fontSize: 13, color: 'var(--gray-500)', marginBottom: 14 }}>
              {popupMode === 'edit'
                ? 'Corrija o número de cones reabastecidos para esta linha.'
                : 'Indique o número de cones reabastecidos para esta linha.'}
            </div>

            <input
              ref={conesRef}
              type="number"
              inputMode="numeric"
              min="0"
              step="1"
              style={{ width: '100%', height: 48, border: '1px solid var(--gray-300)', borderRadius: 10, padding: '0 14px', fontSize: 16, outline: 'none', boxSizing: 'border-box', marginBottom: 14 }}
              placeholder="Ex: 12"
              value={conesInput}
              onChange={(e) => setConesInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') confirmRestock(); }}
              autoFocus
            />

            <button type="button" className="mb-btn-primary" style={{ marginTop: 0 }}
              onClick={confirmRestock} disabled={saving}>
              {saving ? 'A processar…' : 'Confirmar'}
            </button>
          </div>
        </div>
      )}

      {/* Confirm: clear restock */}
      {clearItem && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 16px', boxSizing: 'border-box', zIndex: 200 }}
          onClick={() => { if (!saving) setClearItem(null); }}
        >
          <div style={{ background: 'white', width: '100%', maxWidth: 420, borderRadius: 16, padding: 20, boxShadow: '0 4px 24px rgba(0,0,0,0.25)' }}
            onClick={(e) => e.stopPropagation()}>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>↩️ Anular reabastecimento</div>
            <div style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 13, marginBottom: 6 }}>
              {clearItem.product_code}{clearItem.variant_code ? ` - ${clearItem.variant_code}` : ''}
              {clearItem.lot_no ? ` · ${clearItem.lot_no}` : ''}
            </div>
            <div style={{ fontSize: 13, color: 'var(--gray-600)', marginBottom: 16 }}>
              A linha volta a <strong>Recolhido</strong> e o número de cones
              {clearItem.num_restock_cones != null ? ` (${clearItem.num_restock_cones})` : ''} é removido.
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" disabled={saving} onClick={() => setClearItem(null)}
                style={{ flex: 1, height: 46, border: '1px solid var(--gray-300)', borderRadius: 10, background: 'white', fontSize: 14, fontWeight: 600, color: 'var(--gray-600)', cursor: 'pointer' }}>
                Cancelar
              </button>
              <button type="button" disabled={saving} onClick={confirmClear}
                style={{ flex: 1, height: 46, border: 'none', borderRadius: 10, background: '#dc2626', color: 'white', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
                {saving ? 'A processar…' : 'Anular'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast notifications */}
      <div className="toast-container">
        {toasts.map((toast) => (
          <Toast key={toast.id} id={toast.id} message={toast.message}
            type={toast.type} duration={toast.duration}
            requireConfirm={toast.requireConfirm} onConfirm={toast.onConfirm}
            onClose={removeToast} />
        ))}
      </div>
    </MobileLayout>
  );
};

export default MobileRestock;